import { uuid } from '@utils/common';

import { ETreeTypeNode } from '@enums/tree';

import { FLAT_NODE_PATH_SEPARATOR } from './constants';

import { IBracketStackInterface, IFlatTreeNodeInterface } from './interface';

export const typeOfBrackets = (type?: string) => {
	switch (type) {
		case ETreeTypeNode.array:
			return {
				openedBracket: '[',
				closedBracket: ']',
			};
		case ETreeTypeNode.arrayObjects:
		case ETreeTypeNode.object:
			return {
				openedBracket: '{',
				closedBracket: '}',
			};
		default:
			return {};
	}
};

export function createObjectFromFlatArray(flatArray: IFlatTreeNodeInterface[], ignoredPath?: string) {
	const result = {};
	const skippedFields = [];

	for (const item of flatArray) {
		const { path, value, nodeType } = item;

		if (!item.isChecked && path) {
			skippedFields.push(path);
		}

		const formattedPath = ignoredPath ? path?.replace(ignoredPath, '') : path;

		if (!formattedPath) {
			continue;
		}

		const keys = formattedPath.split(FLAT_NODE_PATH_SEPARATOR).filter((key) => !!key);

		let currentObj: Record<string, any> = result;

		for (let i = 0; i < keys.length - 1; i++) {
			const key = keys[i];

			if (key && !(key in currentObj)) {
				currentObj[key] = {};
			}

			currentObj = currentObj[key];
		}

		const lastKey = keys[keys.length - 1];

		if (nodeType) {
			currentObj[lastKey] = nodeType === ETreeTypeNode.array ? [] : {};
		} else if (Array.isArray(currentObj)) {
			currentObj.push(value);
		} else {
			currentObj[lastKey] = value;
		}
	}

	return { result, skippedFields };
}

export function createFlatArrayFromObject({
	data,
	flatArray = [],
	depth = 0,
	parentPath = '',
	skippedParameters = [],
}: {
	data: Record<string, any>;
	flatArray?: IFlatTreeNodeInterface[];
	depth?: number;
	parentPath?: string;
	skippedParameters?: string[];
}) {
	const keys = Object.keys(data);

	for (const key of keys) {
		const value = data[key];
		const path = parentPath ? `${parentPath}${FLAT_NODE_PATH_SEPARATOR}${key}` : `${key}`;
		const nodeType = Array.isArray(value)
			? ETreeTypeNode.array
			: typeof value === 'object' && value !== null
				? ETreeTypeNode.object
				: undefined;

		const uniqueKey = uuid();
		const isChecked = !skippedParameters.includes(path);

		const arrayValue = value ? value.toString() : JSON.stringify(value);

		flatArray.push({
			key,
			value: nodeType ? undefined : arrayValue,
			nodeType,
			path,
			depth,
			isChecked,
			isCollapsed: false,
			isHiddenChildren: false,
			id: uniqueKey,
			brackets: undefined,
		});

		if (nodeType) {
			createFlatArrayFromObject({
				data: value,
				flatArray,
				depth: depth + 1,
				parentPath: path,
				skippedParameters,
			});
		}
	}

	return flatArray;
}

export const getPathWithoutParents = (parentPath: string, parentKey: string) => {
	if (!parentPath) {
		return;
	}

	const parentKeyPathPosition = parentPath.lastIndexOf(parentKey);

	const pathWithoutParentKey = parentPath.substring(0, parentKeyPathPosition).trim();

	return pathWithoutParentKey.length ? pathWithoutParentKey : undefined;
};

export const getLastNotCollapsedIndex = (array: IFlatTreeNodeInterface[], index: number) => {
	let lastNotCollapsedIndex = index > 0 ? index : 0;

	while (array[lastNotCollapsedIndex].isCollapsed && lastNotCollapsedIndex >= 0) {
		lastNotCollapsedIndex--;
	}

	return !array[lastNotCollapsedIndex].isCollapsed ? lastNotCollapsedIndex : undefined;
};

export const updateParentNodeBrackets = ({
	lastBrackets,
	lastBracketsIndex,
	isOpenedNodeEmpty,
	array,
	parentIndex,
}: {
	lastBrackets?: IBracketStackInterface[];
	lastBracketsIndex?: number;
	isOpenedNodeEmpty?: boolean;
	array: IFlatTreeNodeInterface[];
	parentIndex: number;
}) => {
	//check if not undefined, to ensure that we have nodes need to be updated
	if (lastBracketsIndex === undefined || lastBrackets === undefined || isOpenedNodeEmpty === undefined) return;

	const depthDiff = array[lastBracketsIndex].depth - array[parentIndex].depth;
	const sliceStart = isOpenedNodeEmpty && array[lastBracketsIndex].nodeType
		//if last opened node is empty, we need to remove it's closing bracket by adding 1 to depthDiff
		? depthDiff + 1
		: depthDiff;

	array[lastBracketsIndex].brackets = undefined;
	array[parentIndex].brackets = lastBrackets.slice(sliceStart);
};

export const addClosingBrackets = (array: IFlatTreeNodeInterface[], stack: IBracketStackInterface[], index: number) => {
	if (array[index].nodeType && !array[index].isHiddenChildren) {
		const { closedBracket } = typeOfBrackets(array[index].nodeType);

		//check if last bracket is not the same as current, to avoid duplicates
		if (stack[stack.length - 1]?.depth !== array[index].depth) {
			stack.push({
				depth: array[index].depth,
				bracket: closedBracket,
			});
		}
	}

	//check if next node is exists and different with last stack bracket,
	// to catch the moment when we need to close brackets.
	// Stack bracket depth can be equal to next nodes depth, then we just splice one bracket
	// or it can be bigger so we find last inserted bracket with the same depth as next node
	// and splice all brackets after it
	if (array[index + 1] &&
		stack.length &&
		stack[stack.length - 1].depth >= array[index + 1].depth
	) {
		const found = stack.find(({ depth }) => depth === array[index + 1].depth);
		// get last inserted bracket with the same depth as next node
		const lastEncounterOfDepth = found ? stack.lastIndexOf(found) : undefined;
		// if we found last inserted bracket with the same depth as next node,
		// we splice all brackets after it
		const nodeBrackets = lastEncounterOfDepth !== undefined && stack.splice(lastEncounterOfDepth);

		if (nodeBrackets) {
			//check if node is visible, to avoid adding brackets to nodes that hidden
			if (!array[index].isCollapsed) {
				array[index].brackets = nodeBrackets.reverse();
			} else {
				//if node is hidden, we need to find the last not collapsed node and add brackets to it
				const lastNotCollapsedIndex = getLastNotCollapsedIndex(array, index);

				if (lastNotCollapsedIndex !== undefined) {
					array[lastNotCollapsedIndex].brackets = nodeBrackets.reverse();
				}
			}
		}
		//clean up node brackets in case it has brackets
	} else array[index].brackets = undefined;

	//if its end of array and stack is not empty, we need to add brackets to the last not collapsed node
	if (index === array.length - 1 && stack.length) {
		const lastNotCollapsedIndex = getLastNotCollapsedIndex(array, index);

		if (lastNotCollapsedIndex !== undefined) {
			array[lastNotCollapsedIndex].brackets = stack.reverse();
		}
	}
};

export const toggleFlatTree = (array: IFlatTreeNodeInterface[], id: string, parentPath: string) => {
	const parentIndex = array.findIndex((item) => item.id === id);

	if (parentIndex < 0) return;

	const parentNode = array[parentIndex];
	//toggle collapse/expand
	parentNode.isHiddenChildren = !parentNode.isHiddenChildren;

	const isCollapsing = parentNode.isHiddenChildren;
	//store last encountered brackets if they exist, when collapsing.
	let lastBrackets: IBracketStackInterface[] | undefined;
	//store index of the last node that has brackets, when collapsing.
	let lastBracketsIndex: number | undefined;
	//indicates if the last node is opened and empty, when collapsing.
	// if so, we have to remove +1 bracket from lastBrackets before setting it in updateTailBrackets
	let isOpenedNodeEmpty: boolean | undefined;

	// since we can start from any node, stack can not have all brackets we need,
	// by calculating it in addClosingBrackets, so we take brackets from parent if they exist
	const stack: IBracketStackInterface[] = parentNode?.brackets
		? isCollapsing
			// If parent node has brackets when collapsing, it indicates that the parent node is empty.
			// In this case, we remove the closing bracket of the parent from
			// the stack by slicing the first element.
			// Additionally, the brackets need to be reversed before adding them to the stack,
			// as the stack will be reversed later in the "addClosingBrackets" function.
			? parentNode.brackets.slice(1).map((bracket) => ({ ...bracket })).reverse()
			// when expanding, we need to add all the brackets of the parent node to the stack.
			// so last child will have all closing brackets of the parent node
			: parentNode.brackets.map((bracket) => ({ ...bracket })).reverse()
		: [];

	for (let index = parentIndex; index < array.length; index++) {
		const node = array[index];
		//check if node has common path with parent node
		const isCommonRoot = node.path.startsWith(parentPath + FLAT_NODE_PATH_SEPARATOR)
			|| node.path === parentPath;

		if (!isCommonRoot) {
			// if collapsing we have to add closing brackets to the last node
			if (isCollapsing) {
				// since we do not know last child of the parent node, we get last node with common path
				// and add its closing brackets to the parent
				updateParentNodeBrackets({
					array: array,
					lastBrackets,
					lastBracketsIndex,
					isOpenedNodeEmpty,
					parentIndex,
				});
			}

			return;
		}

		// if node has common path with parent node
		if (isCommonRoot) {
			// if collapsing, and current node is not parent node, and its depth is greater than parent node
			if (isCollapsing && node.path !== parentPath && node.depth > parentNode.depth) {
				if (!node.isCollapsed) {
					//We store the index of the last opened element and its brackets because
					// later in the loop, we recalculate the brackets and set isCollapsed to false.
					lastBracketsIndex = index;

					// if node is not collapsed, we have to store its brackets
					lastBrackets = node.brackets;

					// check if node is empty and opened,if so we have to remove +1 bracket
					// from lastBrackets before setting it in updateTailBrackets
					isOpenedNodeEmpty = !node.isHiddenChildren && !!node.brackets;
				}

				// set node as collapsed
				node.isCollapsed = true;
				node.isHiddenChildren = true;
			} else if (!isCollapsing && node.depth === parentNode.depth + 1) node.isCollapsed = false;
		}

		// adding closing brackets, based on stack
		addClosingBrackets(array, stack, index);
	}

	if (isCollapsing) {
		// in case we run through all the nodes and did not break loop when collapse,
		// we have to lastBrackets them to the last node
		updateParentNodeBrackets({
			array: array,
			lastBrackets,
			lastBracketsIndex,
			isOpenedNodeEmpty,
			parentIndex,
		});
	}
};

const formatSkippedFields = (skippedFields: string[], originalSeparator?: string) => {
	return originalSeparator
		? skippedFields.map((parameter) => parameter.replaceAll(originalSeparator, FLAT_NODE_PATH_SEPARATOR))
		: skippedFields;
};

export const convertToFlatTreeData = (
	data: Record<string, any>,
	skippedParameters: string[] = [],
	objectWrapper?: string,
	originalSeparator?: string,
) => {
	const formattedSkippedParameters = formatSkippedFields(skippedParameters, originalSeparator);

	const array = createFlatArrayFromObject({
		data: objectWrapper ? { [objectWrapper]: data } : data,
		skippedParameters: formattedSkippedParameters,
	});

	const stack: IBracketStackInterface[] = [];

	for (let index = 0; index < array.length; index++) {
		addClosingBrackets(array, stack, index);
	}

	return array;
};
